import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import Breadcrumbs from "@/components/admin/Breadcrumbs";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { formatApiError } from "@/lib/errors";

export default function PlatformApps() {
  const [data, setData] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);
  const load = async () => {
    const r = await api.get("/platform/app-matrix");
    setData(r.data); setDirty(false);
  };

  const toggle = (appId, tenantId) => {
    setData(prev => ({
      ...prev,
      matrix: {
        ...prev.matrix,
        [appId]: { ...prev.matrix[appId], [tenantId]: !prev.matrix[appId][tenantId] },
      },
    }));
    setDirty(true);
  };

  const save = async () => {
    const baseline = await api.get("/platform/app-matrix");
    const changes = [];
    for (const a of data.apps) {
      for (const t of data.tenants) {
        const before = baseline.data.matrix[a.id][t.id];
        const after = data.matrix[a.id][t.id];
        if (before !== after) changes.push({ tenant_id: t.id, app_id: a.id, allow: after });
      }
    }
    if (changes.length === 0) { toast.info("No changes"); setDirty(false); return; }
    setSaving(true);
    try {
      const { data: res } = await api.post("/platform/app-matrix", { changes });
      toast.success(`Saved: ${res.granted} granted, ${res.revoked} revoked`);
      load();
    } catch (e) { toast.error(formatApiError(e) || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="p-8 text-sm text-slate-500">Loading…</div>;

  return (
    <div data-testid="platform-apps" className="mx-auto w-full max-w-6xl space-y-6">
      <Breadcrumbs />
      <div className="flex items-end justify-between">
        <div>
          <h2 className="font-display text-2xl font-semibold text-slate-900">App allocation</h2>
          <p className="mt-1 text-sm text-slate-500">Grant registered apps to tenants. Apps are platform-owned.</p>
        </div>
        <div className="flex items-center gap-2">
          {dirty && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-800">Unsaved</span>}
          <Button onClick={save} disabled={!dirty || saving} data-testid="matrix-save" className="bg-slate-900 hover:bg-slate-800">{saving ? "Saving…" : "Save"}</Button>
        </div>
      </div>
      <div className="overflow-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="sticky left-0 bg-slate-50 px-4 py-2 text-left text-[11px] uppercase tracking-wider text-slate-500">App \\ Tenant</th>
              {data.tenants.map(t => (
                <th key={t.id} className="px-3 py-2 text-center text-[11px] font-semibold text-slate-600">
                  <div className="flex flex-col items-center gap-1">
                    <span className="inline-block h-2 w-2 rounded-full" style={{ background: t.primary_color }} />
                    {t.name}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.apps.map(a => (
              <tr key={a.id} className="border-t border-slate-100">
                <td className="sticky left-0 bg-white px-4 py-2 font-medium text-slate-800">{a.name}</td>
                {data.tenants.map(t => (
                  <td key={t.id} className="px-3 py-2 text-center" data-testid={`matrix-cell-${a.id}-${t.slug}`}>
                    <Checkbox checked={data.matrix[a.id][t.id]} onCheckedChange={() => toggle(a.id, t.id)} />
                  </td>
                ))}
              </tr>
            ))}
            {data.apps.length === 0 && <tr><td colSpan={data.tenants.length + 1} className="p-8 text-center text-slate-400">No apps registered. Use /admin/apps to add one.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
